import { useEffect, useState } from 'react';

import { fg } from '@confluence/feature-gating';
import { expValEquals } from '@confluence/feature-experiments';

/**
 * Marketplace apps that can show at the top of the page in the ATL General web panel.
 * This height of the panel is set in useATLGeneralWebPanelObserver and the event atlGeneralWebPanelHeightUpdated is emitted there
 *
 * @returns The height of the ATL General web panel in pixels.
 */
export const useGeneralWebPanelHeight = (): number => {
	const [panelHeight, setPanelHeight] = useState<number>(() => {
		if (
			typeof window !== 'undefined' &&
			Boolean(window.__SSR_RENDERED__) &&
			expValEquals('confluence_editor_wrapper_ttvc_fix', 'isEnabled', true)
		) {
			const initialHeight = window['__ATL_GENERAL_WEBPANEL_INITIAL_HEIGHT__'];
			return initialHeight ?? 0;
		}

		return 0;
	});

	useEffect(() => {
		if (!fg('confluence_editor_height_when_atl_header')) {
			return;
		}

		const updateHeight = (e?: any) => {
			if (expValEquals('confluence_editor_wrapper_ttvc_fix', 'isEnabled', true)) {
				// Use event detail height if available, otherwise fallback to 0
				setPanelHeight(e?.detail?.height ?? 0);
			} else {
				// Legacy behavior: read from CSS variable
				try {
					const heightValue = getComputedStyle(document.documentElement).getPropertyValue(
						'--atl-general-webpanel-height',
					);
					const numericValue = parseInt(heightValue, 10);

					setPanelHeight(!isNaN(numericValue) && numericValue >= 0 ? numericValue : 0);
				} catch (e) {
					setPanelHeight(0);
				}
			}
		};

		!expValEquals('confluence_editor_wrapper_ttvc_fix', 'isEnabled', true) && updateHeight();

		// Listen for height changes
		document.addEventListener('atlGeneralWebPanelHeightUpdated', updateHeight);
		return () => {
			document.removeEventListener('atlGeneralWebPanelHeightUpdated', updateHeight);
		};
	}, []);

	return panelHeight;
};
